/**
 * Merchant self-onboarding field configuration (PE-2181, backend PE-2180).
 *
 * Mirrors watchman's `MerchantOnboardingFieldKey` / `MerchantOnboardingFieldRequirement`
 * enums (prisma/schema/merchant-onboarding-field-config.model.prisma). One GLOBAL row per
 * field key — this is not per-merchant and not per-contract-template; it directly governs
 * `POST /merchants/self-onboard`'s presence validation.
 */

export const MerchantOnboardingFieldKey = {
  BUSINESS_NAME: 'BUSINESS_NAME',
  COMPANY_ADDRESS: 'COMPANY_ADDRESS',
  CONTACT_FULL_NAME: 'CONTACT_FULL_NAME',
  CONTACT_EMAIL: 'CONTACT_EMAIL',
  CONTACT_PHONE_NUMBER: 'CONTACT_PHONE_NUMBER',
  INDUSTRY_IDS: 'INDUSTRY_IDS',
  BUSINESS_CATEGORY_IDS: 'BUSINESS_CATEGORY_IDS',
  BANK_BUSINESS_NAME: 'BANK_BUSINESS_NAME',
  BANK_CODE: 'BANK_CODE',
  BANK_ACCOUNT_NUMBER: 'BANK_ACCOUNT_NUMBER',
} as const;

export type MerchantOnboardingFieldKey =
  (typeof MerchantOnboardingFieldKey)[keyof typeof MerchantOnboardingFieldKey];

export const MerchantOnboardingFieldRequirement = {
  REQUIRED: 'REQUIRED',
  OPTIONAL: 'OPTIONAL',
} as const;

export type MerchantOnboardingFieldRequirement =
  (typeof MerchantOnboardingFieldRequirement)[keyof typeof MerchantOnboardingFieldRequirement];

export interface MerchantOnboardingField {
  fieldKey: MerchantOnboardingFieldKey;
  requirement: MerchantOnboardingFieldRequirement;
  /** True when no admin override exists and `requirement` is the documented platform default. */
  isDefault: boolean;
  /** When the override was last changed, or null while `isDefault` is true. */
  updatedAt: string | null;
}

export interface SetMerchantOnboardingFieldsPayload {
  /** Upserts ONLY the listed keys — omitted keys keep their existing state. */
  fields: { fieldKey: MerchantOnboardingFieldKey; requirement: MerchantOnboardingFieldRequirement }[];
}

/**
 * watchman rejects OPTIONAL for these three with a 400: the merchant/user-creation
 * transaction consumes them unconditionally (slug generation, the unique login email, the
 * user's full name), so there is no fallback when they are blank. `INDUSTRY_IDS` and
 * `BUSINESS_CATEGORY_IDS` also default to REQUIRED but CAN be relaxed.
 * Source: watchman `NON_OVERRIDABLE_TO_OPTIONAL` in merchant-onboarding-fields.service.ts.
 */
export const ALWAYS_REQUIRED_FIELD_KEYS: MerchantOnboardingFieldKey[] = [
  MerchantOnboardingFieldKey.BUSINESS_NAME,
  MerchantOnboardingFieldKey.CONTACT_EMAIL,
  MerchantOnboardingFieldKey.CONTACT_FULL_NAME,
];

/** Labels mirror watchman's FIELD_LABELS so an error message names the same thing the row does. */
export const FIELD_LABELS: Record<MerchantOnboardingFieldKey, string> = {
  BUSINESS_NAME: 'Business name',
  COMPANY_ADDRESS: 'Company address',
  CONTACT_FULL_NAME: 'Contact full name',
  CONTACT_EMAIL: 'Contact email',
  CONTACT_PHONE_NUMBER: 'Contact phone number',
  INDUSTRY_IDS: 'Industries',
  BUSINESS_CATEGORY_IDS: 'Business categories',
  BANK_BUSINESS_NAME: 'Bank account business name',
  BANK_CODE: 'Bank code',
  BANK_ACCOUNT_NUMBER: 'Bank account number',
};

/** What the merchant actually fills in on the public sign-up form, for the row's sub-label. */
export const FIELD_DESCRIPTIONS: Record<MerchantOnboardingFieldKey, string> = {
  BUSINESS_NAME: 'Trading name — also becomes the storefront slug.',
  COMPANY_ADDRESS: 'Registered business address.',
  CONTACT_FULL_NAME: "The signatory's full name.",
  CONTACT_EMAIL: 'Becomes the merchant admin login.',
  CONTACT_PHONE_NUMBER: 'Phone number for the primary contact.',
  INDUSTRY_IDS: 'One or more industries the business trades in.',
  BUSINESS_CATEGORY_IDS: 'One or more business categories.',
  BANK_BUSINESS_NAME: 'Account name as registered with the bank.',
  BANK_CODE: 'Settlement bank.',
  BANK_ACCOUNT_NUMBER: 'Settlement account number.',
};
